import { computed, ref, watch } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { defineStore } from 'pinia';

import apiGw from '@/services/apiGw';
import { useContextSwitchStore } from '@/store/contextSwitchStore';
import { createResourceUri } from '@/utils/links';
import {
  PartnershipDeputyChange,
  PartnershipMember,
  PartnershipMemberDetails,
  PartnershipURI,
  Role
} from '@/types/partnership';
import { usePartnershipsStore } from '@/store/partnershipsStore';
import {
  Invitation,
  TeamSpaceView,
  MemberAction,
  MembersActionId,
  RoleRemovalImpact,
  MemberActionConfirmModal
} from '@/types/teamSpace';
import { SseListener } from '@/utils/general';
import { useEventStreamStore } from '@/store/eventStreamStore';
import { RouteNames } from '@/router/routes';
import { useI18n } from '@/plugins/i18n';
import { AlertTypes, useAlertStore } from '@/store/alertsStore';
import { useUserStore } from '@/store/userStore';

export const useTeamSpaceStore = defineStore('teamSpace', () => {
  const partnershipsStore = usePartnershipsStore();
  const contextStore = useContextSwitchStore();
  const eventStreamStore = useEventStreamStore();
  const alertsStore = useAlertStore();
  const userStore = useUserStore();

  const route = useRoute();
  const router = useRouter();
  const { t } = useI18n();

  // STATES
  const isLoading = ref<TeamSpaceView | undefined>(undefined);
  const isLoadingAction = ref<boolean>(false);
  const members = ref<PartnershipMember[]>([]);
  const deputyChange = ref<PartnershipDeputyChange | undefined>(undefined);
  const selectedMember = ref<PartnershipMemberDetails>({});
  const invitations = ref<Invitation[]>([]);
  const showConfirmModal = ref<boolean>(false);
  const confirmModalText = ref<MemberActionConfirmModal | undefined>(undefined);
  const selectedAction = ref<(() => void) | undefined>(undefined);

  // GETTERS
  const selectedMemberUid = computed(() => {
    const { id, view } = router.currentRoute.value.params;
    return id && view === TeamSpaceView.Members ? id : undefined;
  });

  function getMemberByUid(uid: string) {
    if (!uid) return undefined;
    return members.value.find((member) => member.personInfo.uid === uid);
  }

  function getDeputyChangeStatus(uid: string): { isBeingAdded: boolean; isBeingRemoved: boolean } {
    if (!deputyChange.value) {
      return { isBeingAdded: false, isBeingRemoved: false };
    }
    const changeStatus =
      deputyChange.value?.changeData?.changeStatus === 'approve' ||
      deputyChange.value?.changeData?.changeStatus === 'edit';

    return {
      isBeingAdded: changeStatus && deputyChange.value.deputiesToAdd.includes(uid),
      isBeingRemoved: changeStatus && deputyChange.value.deputiesToRemove.includes(uid)
    };
  }

  function getDropdownEntries(row: PartnershipMember) {
    return [
      {
        id: 'row-actions',
        items: [
          {
            id: MembersActionId.AdminRemove,
            name: t('app.partnership.deputy_remove.action'),
            icon: 'trash-outline',
            hover: 'text-yapred-500 dark:text-yapred-400',
            action: () =>
              openConfirmModal(MembersActionId.AdminRemove, row, () =>
                addRemoveAdmin(row.personInfo.uid, MemberAction.Remove)
              ),
            displayCondition:
              row.roleTypes.includes(Role.Deputy) &&
              (partnershipsStore.hasRoleForCurrentPartnership(Role.Partner) ||
                partnershipsStore.hasRoleForCurrentPartnership(Role.Deputy))
          },
          {
            id: MembersActionId.AdminAdd,
            name: t('app.partnership.deputy_nominate'),
            icon: 'checkmark-outline',
            action: () =>
              openConfirmModal(MembersActionId.AdminAdd, row, () =>
                addRemoveAdmin(row.personInfo.uid, MemberAction.Add)
              ),
            displayCondition:
              !row.roleTypes.includes(Role.Deputy) &&
              row.roleTypes.includes(Role.Delegate) &&
              !row.personInfo.onboardingDetails?.isRegistered &&
              (partnershipsStore.hasRoleForCurrentPartnership(Role.Partner) ||
                partnershipsStore.hasRoleForCurrentPartnership(Role.Deputy))
          },
          {
            id: MembersActionId.SelfRequestRights,
            name: t('webapp.partnership.request-access'),
            icon: 'checkmark-outline',
            action: () => openConfirmModal(MembersActionId.SelfRequestRights, row, undefined),
            displayCondition:
              !row.roleTypes.includes(Role.Deputy) &&
              !row.roleTypes.includes(Role.Delegate) &&
              row.roleTypes.includes(Role.Partner) &&
              userStore.uid === row.personInfo.uid
          },
          {
            id: MembersActionId.MemberRemove,
            name: t('app.partnership.delegate_remove.action'),
            icon: 'trash-outline',
            hover: 'text-yapred-500 dark:text-yapred-400',
            action: () =>
              openConfirmModal(MembersActionId.MemberRemove, row, () =>
                removeMemberRole(row.personInfo.uid, Role.Delegate)
              ),
            displayCondition: row.roleTypes.includes(Role.Delegate),
            disabled: row.roleTypes.includes(Role.Deputy),
            tooltip: row.roleTypes.includes(Role.Deputy) ? t('app.partnership.deputy.member_removing_alert') : ''
          },
          {
            id: MembersActionId.SignatoryRemove,
            name: t('app.partnership.confirmationPurpose.reduction'),
            icon: 'trash-outline',
            hover: 'text-yapred-500 dark:text-yapred-400',
            action: () =>
              openConfirmModal(MembersActionId.SignatoryRemove, row, () =>
                removeMemberRole(row.personInfo.uid, Role.Partner)
              ),
            displayCondition: row.roleTypes.includes(Role.Partner),
            disabled: row.roleTypes.includes(Role.Deputy),
            tooltip: row.roleTypes.includes(Role.Deputy) ? t('app.partnership.deputy.member_removing_alert') : ''
          }
        ].filter((item) => item.displayCondition === undefined || item.displayCondition)
      }
    ];
  }

  // ACTIONS
  function setMembers(mem: PartnershipMember[]) {
    members.value = mem;
  }

  function setDeputyChange(dc: PartnershipDeputyChange) {
    deputyChange.value = dc;
  }

  function setSelectedMember(mem: PartnershipMemberDetails) {
    selectedMember.value = mem;
  }

  function setInvitations(inv: Invitation[]) {
    invitations.value = inv;
  }

  async function fetchMembers() {
    if (!partnershipsStore.currentPartnership) return;
    try {
      isLoading.value = TeamSpaceView.Members;
      const res = await apiGw.get(
        createResourceUri(PartnershipURI.BaseV2, {
          uid: partnershipsStore.currentPartnership?.uid,
          resource: PartnershipURI.Members,
          queryParams: {
            orderByName: true
          }
        })
      );

      setMembers(
        res.data
          .filter((member: PartnershipMember) => {
            return (
              member.roleTypes.includes(Role.Partner) ||
              member.roleTypes.includes(Role.Delegate) ||
              member.roleTypes.includes(Role.Deputy)
            );
          })
          .sort((a: PartnershipMember, b: PartnershipMember) => {
            const aRegistered = a.personInfo.onboardingDetails?.isRegistered ? 1 : 0;
            const bRegistered = b.personInfo.onboardingDetails?.isRegistered ? 1 : 0;
            return aRegistered - bRegistered;
          })
      );
    } catch (error) {
      console.error('Error loading members', error);
    } finally {
      isLoading.value = undefined;
    }
  }

  async function fetchDeputyChange() {
    if (!partnershipsStore.currentPartnership) return;
    try {
      const res = await apiGw.get(
        createResourceUri(PartnershipURI.BaseV2, {
          uid: partnershipsStore.currentPartnership?.uid,
          resource: PartnershipURI.DeputyChange
        })
      );

      setDeputyChange(res.data);
    } catch (error) {
      console.error('Error loading deputy change', error);
    }
  }

  async function fetchMemberDetails() {
    if (!selectedMemberUid.value || !partnershipsStore.currentPartnership?.uid) return;
    try {
      isLoading.value = TeamSpaceView.MemberDetails;
      const res = await apiGw.get(
        createResourceUri(PartnershipURI.BaseV2, {
          uid: partnershipsStore.currentPartnership?.uid,
          resource: `${PartnershipURI.Members}/${selectedMemberUid.value}/details`
        })
      );

      setSelectedMember(res.data);
    } catch (error) {
      console.error('Error loading member details', error);
    } finally {
      isLoading.value = undefined;
    }
  }

  async function fetchInvitations() {
    if (!partnershipsStore.currentPartnership) return;
    try {
      isLoading.value = TeamSpaceView.Invitations;

      const res = await apiGw.post(createResourceUri('/v2/codes'), {
        openOnly: true,
        partnershipUid: partnershipsStore.currentPartnership?.uid,
        requestedByCase: true
      });

      setInvitations(res.data);
    } catch (error) {
      console.error('Error loading invitations', error);
    } finally {
      isLoading.value = undefined;
    }
  }

  async function openConfirmModal(id: MembersActionId, row: PartnershipMember, action?: () => void) {
    if (id === MembersActionId.AdminRemove) {
      confirmModalText.value = {
        id: MembersActionId.AdminRemove,
        title: t('app.partnership.deputy_remove.action'),
        text: t('app.partnership.deputy_remove.body', [row.personInfo.name])
      };
    } else if (id === MembersActionId.AdminAdd) {
      confirmModalText.value = {
        id: MembersActionId.AdminAdd,
        title: t('app.partnership.deputy_nominate'),
        text: t('webapp.partnership.deputy-nominate.body', [row.personInfo.name])
      };
    } else if (id === MembersActionId.MemberRemove) {
      const res = await apiGw.post(
        createResourceUri(PartnershipURI.BaseV2, {
          uid: partnershipsStore.currentPartnership?.uid,
          resource: `${PartnershipURI.Agent}/${row.personInfo.uid}${PartnershipURI.Delegate}${PartnershipURI.Impact}`
        })
      );
      const { delegateReductionImpact } = res.data as RoleRemovalImpact;
      confirmModalText.value = {
        id: MembersActionId.MemberRemove,
        title: t('app.partnership.delegate_remove.action'),
        text: [
          `${t('app.partnership.removingImpact.subtitle', [t('app.partnership.member').toLowerCase()])}`,
          `**${row.personInfo.name}**`,
          `${t('app.partnership.removingImpact.explanation')}`
        ],
        data: {
          numCardPrograms: delegateReductionImpact?.numCardPrograms,
          numDebitCards: delegateReductionImpact?.numDebitCards
        }
      };
    } else if (id === MembersActionId.SignatoryRemove) {
      const res = await apiGw.post(
        createResourceUri(PartnershipURI.BaseV2, {
          uid: partnershipsStore.currentPartnership?.uid,
          resource: `${PartnershipURI.Agent}/${row.personInfo.uid}${PartnershipURI.Partner}${PartnershipURI.Impact}`
        })
      );

      const { partnerReductionImpact } = res.data as RoleRemovalImpact;

      if (partnerReductionImpact && Object.keys(partnerReductionImpact).length === 0) {
        return alertsStore.addAlert({
          id: 'remove-member-failure',
          message: t('app.partnership.partnerRemoval.failure', [
            t('app.partnership.controllingPerson.invite.partner').toLowerCase()
          ]),
          dismissable: true,
          alertType: AlertTypes.Error,
          alertTimestamp: Date.now(),
          autoDismissDelay: 5000
        });
      }

      if (
        partnerReductionImpact &&
        'resultingVotingWeight' in partnerReductionImpact &&
        partnerReductionImpact.resultingVotingWeight < 1
      ) {
        confirmModalText.value = {
          id: MembersActionId.MemberRemove,
          title: t('app.partnership.confirmationPurpose.reduction'),
          text: [`**${row.personInfo.name}**`, t('app.partnership.removingImpact.canNotBeRemoved')],
          disabledAction: true
        };
      } else {
        confirmModalText.value = {
          id: MembersActionId.MemberRemove,
          title: t('app.partnership.confirmationPurpose.reduction'),
          text: [
            `${t('app.partnership.removingImpact.subtitle', [t('app.partnership.controllingPerson.invite.partner').toLowerCase()])}`,
            `**${row.personInfo.name}**`,
            `${t('webapp.partnership.partner.removingImpact')}`
          ]
        };
      }
    } else if (id === MembersActionId.SelfRequestRights) {
      confirmModalText.value = {
        id: MembersActionId.SelfRequestRights,
        title: t('webapp.partnership.request-access')
      };
    }

    selectedAction.value = action;
    showConfirmModal.value = true;
  }

  async function addRemoveAdmin(uid: string, action: MemberAction) {
    const isAddAction = action === MemberAction.Add;

    try {
      isLoadingAction.value = true;

      const data = {
        done: true,
        start: true,
        deputiesToAdd: isAddAction ? [uid] : [],
        deputiesToRemove: !isAddAction ? [uid] : [],
        agentsToAddAllRights: isAddAction ? [uid] : [],
        agentsToRemoveAllRights: !isAddAction ? [uid] : []
      };

      await apiGw.post(
        createResourceUri(PartnershipURI.BaseV2, {
          uid: partnershipsStore.currentPartnership?.uid,
          resource: PartnershipURI.DeputyChange
        }),
        data
      );
      selectedAction.value = undefined;
      showConfirmModal.value = false;
      alertsStore.addAlert({
        id: 'nominate-admin-success',
        message: isAddAction ? 'app.partnership.deputy_add.success' : 'app.partnership.deputy_remove.success',
        dismissable: true,
        alertType: AlertTypes.Success,
        alertTimestamp: Date.now(),
        autoDismissDelay: 5000
      });
    } catch (error) {
      alertsStore.addAlert({
        id: 'nominate-admin-failure',
        message: isAddAction ? 'app.partnership.deputy_add.failure' : 'app.partnership.deputy_remove.failure',
        dismissable: true,
        alertType: AlertTypes.Error,
        alertTimestamp: Date.now(),
        autoDismissDelay: 5000
      });
      console.error(`Unable to ${action} admin`, error);
    } finally {
      isLoadingAction.value = false;
    }
  }

  async function removeMemberRole(uid: string, role: Role) {
    try {
      isLoadingAction.value = true;

      await apiGw.post(
        createResourceUri(PartnershipURI.BaseV2, {
          uid: partnershipsStore.currentPartnership?.uid,
          resource: `/${role}/${uid}${PartnershipURI.Revoke}`
        })
      );
      alertsStore.addAlert({
        id: 'remove-member-success',
        message: t('app.partnership.partnerRemoval.success', [
          role === Role.Partner
            ? t('app.partnership.controllingPerson.invite.partner').toLowerCase()
            : t('app.partnership.member').toLowerCase()
        ]),
        dismissable: true,
        alertType: AlertTypes.Success,
        alertTimestamp: Date.now(),
        autoDismissDelay: 5000
      });
      selectedAction.value = undefined;
      showConfirmModal.value = false;
    } catch (error) {
      alertsStore.addAlert({
        id: 'remove-member-failure',
        message: t('app.partnership.partnerRemoval.failure', [
          role === Role.Partner
            ? t('app.partnership.controllingPerson.invite.partner').toLowerCase()
            : t('app.partnership.member').toLowerCase()
        ]),
        dismissable: true,
        alertType: AlertTypes.Error,
        alertTimestamp: Date.now(),
        autoDismissDelay: 5000
      });
      console.error(`Unable to remove ${role} role`, error);
    } finally {
      isLoadingAction.value = false;
    }
  }

  function $reset() {
    isLoadingAction.value = false;
    isLoading.value = undefined;
    members.value = [];
    selectedMember.value = {};
    invitations.value = [];
    showConfirmModal.value = false;
    confirmModalText.value = undefined;
    selectedAction.value = undefined;
  }

  watch(
    () => partnershipsStore.currentPartnership?.uid,
    async (newVal, oldVal) => {
      if (newVal === oldVal || route.name === RouteNames.Login) return;

      if (contextStore.isPrivateContext) {
        $reset();
        router.push({ name: RouteNames.Dashboard });
        return;
      }

      $reset();

      if (route.params.view === TeamSpaceView.Invitations) {
        router.push({ params: { view: TeamSpaceView.Invitations, id: '' } });
        await fetchInvitations();
      } else if (route.params.view === TeamSpaceView.Members) {
        Promise.all([
          router.push({ params: { view: TeamSpaceView.Members, id: '' } }),
          fetchMembers(),
          fetchDeputyChange()
        ]);
      }
    }
  );

  watch(
    () => eventStreamStore.stale_agent_in_partnership,
    (newVal) => {
      const currentUid = partnershipsStore.currentPartnership?.uid;
      if (!newVal || (currentUid && !newVal[currentUid])) return;

      Promise.all([fetchMembers(), fetchDeputyChange(), partnershipsStore.loadAdditionalPartnershipData()]);

      if (selectedMemberUid.value) fetchMemberDetails();

      if (currentUid) {
        eventStreamStore.unstaleUid(currentUid, SseListener.AgentInPartnership);
      }
    },
    { deep: true }
  );

  return {
    // STATES
    isLoading,
    isLoadingAction,
    members,
    selectedMember,
    invitations,
    showConfirmModal,
    confirmModalText,
    selectedAction,

    // GETTERS
    selectedMemberUid,
    getDeputyChangeStatus,
    getMemberByUid,
    getDropdownEntries,

    // ACTIONS
    setMembers,
    setSelectedMember,
    setInvitations,
    fetchMembers,
    fetchDeputyChange,
    fetchMemberDetails,
    fetchInvitations,
    openConfirmModal,
    addRemoveAdmin,
    removeMemberRole,
    $reset
  };
});
